import React, { useEffect, useState } from 'react';
import { Flame, Frown, X, Brain, Search, Dumbbell, CalendarHeart, Sparkles } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface StreakModalProps {
  isOpen: boolean;
  onClose: () => void;
  crimeStreak: number;
  trainingStreak: number;
  wordDayStreak?: number;
  buttonRef?: React.RefObject<HTMLDivElement>;
  crimeHistory?: boolean[]; // Array of 7 booleans (last 6 days + today)
  trainingHistory?: boolean[];
  wordDayHistory?: boolean[]; // Array of 7 booleans (last 6 days + today)
}

const WeekStatus: React.FC<{ history?: boolean[]; activeColorClass: string }> = ({ history, activeColorClass }) => {
  const days = ['S', 'M', 'T', 'W', 'T', 'F', 'S']; // Generic labels
  // We need to rotate labels to match "Last 7 days" ending in Today.
  // Actually, "Last 7 days" is simpler. We just show 7 circles.
  // If we want to label them by day of week, we need to know what Today is.
  const todayIndex = new Date().getDay(); // 0 = Sun, 6 = Sat

  // Reorder days so the last one is todayIndex
  const rotatedDays = [];
  for (let i = 6; i >= 0; i--) {
    const dIndex = (todayIndex - i + 7) % 7;
    rotatedDays.push(days[dIndex]);
  }

  // history is [Day-6, Day-5, ..., Today]
  const displayHistory = history || new Array(7).fill(false);
  const isToday = (index: number) => index === displayHistory.length - 1; // Last item is today

  return (
    <div className="flex justify-between items-center mt-3 md:mt-4 px-0.5 md:px-1">
      {displayHistory.map((isActive, index) => {
        const today = isToday(index);
        return (
          <div key={index} className="flex flex-col items-center gap-1">
            <span className={`text-[9px] md:text-[10px] font-bold ${today ? 'text-gray-700' : 'text-gray-400'}`}>
              {rotatedDays[index]}
            </span>
            <div
              className={`w-6 h-6 md:w-8 md:h-8 rounded-full flex items-center justify-center transition-all ${
                isActive
                  ? `${activeColorClass} text-white shadow-sm ${today ? 'ring-2 ring-offset-1 ring-white/30' : ''}`
                  : today
                  ? 'bg-gray-200 border-2 border-gray-400'
                  : 'bg-gray-100'
              }`}
            >
              {isActive ? (
                <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-white rounded-full" />
              ) : today ? (
                <div className="w-1.5 h-1.5 md:w-2 md:h-2 bg-gray-400 rounded-full" />
              ) : null}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const StreakModal: React.FC<StreakModalProps> = ({
  isOpen,
  onClose,
  crimeStreak,
  trainingStreak,
  wordDayStreak = 0,
  buttonRef,
  crimeHistory,
  trainingHistory,
  wordDayHistory
}) => {
  const navigate = useNavigate();
  const [position, setPosition] = useState({ 
    top: 0, 
    right: 0, 
    isMobile: window.innerWidth < 768 
  });

  useEffect(() => {
    const updatePosition = () => {
      if (isOpen) {
        const isMobile = window.innerWidth < 768;
        
        if (isMobile) {
          setPosition({ top: 0, right: 0, isMobile: true });
        } else if (buttonRef?.current) {
          const rect = buttonRef.current.getBoundingClientRect();
          const navHeight = rect.bottom;
          const buttonRight = rect.right;
          
          setPosition({
            top: navHeight + 4, // Small gap below header
            right: Math.max(16, window.innerWidth - buttonRight), // Align with button, min 16px from edge
            isMobile: false
          });
        }
      }
    };

    if (isOpen) {
      updatePosition();
      window.addEventListener('resize', updatePosition);
      window.addEventListener('scroll', updatePosition, true);
      
      return () => {
        window.removeEventListener('resize', updatePosition);
        window.removeEventListener('scroll', updatePosition, true);
      };
    }
  }, [isOpen, buttonRef]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 z-40 bg-black/20 backdrop-blur-sm" 
        onClick={onClose}
      />
      
      {/* Modal positioned below header */}
      <div
        className={`fixed z-50 bg-white rounded-2xl shadow-2xl transition-all w-[calc(100vw-2rem)] md:w-auto md:max-w-sm ${
          position.isMobile
            ? 'top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2'
            : ''
        }`}
        style={
          position.isMobile
            ? { maxHeight: 'calc(100vh - 2rem)', maxWidth: '384px' }
            : {
                top: `${position.top}px`,
                right: `${position.right}px`,
                maxHeight: `calc(100vh - ${position.top}px - 1rem)`,
                width: '384px',
                maxWidth: 'calc(100vw - 2rem)'
              }
        }
        onClick={(e) => e.stopPropagation()}
      >
        <div className="overflow-y-auto max-h-full rounded-2xl">
          <div className="relative p-4 md:p-6">
            <button
              onClick={onClose}
              className="absolute top-3 right-3 z-20 p-1 text-gray-400 hover:bg-gray-100 rounded-full transition-colors"
            >
              <X className="w-5 h-5" />
            </button>

            <h2 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-2">
              Your Streaks
            </h2>

            <div className="space-y-4 md:space-y-6">
              {/* Crime Game Section */}
              <div className="rounded-xl p-3 md:p-4 border bg-orange-50 border-orange-200 transition-colors">
                <div className="flex justify-between items-center mb-2 md:mb-3">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="p-1.5 md:p-2 rounded-lg bg-orange-100">
                      {crimeStreak > 0 ? (
                        <Flame className="w-5 h-5 md:w-6 md:h-6 text-orange-500 fill-orange-500 animate-pulse" />
                      ) : (
                        <Search className="w-5 h-5 md:w-6 md:h-6 text-orange-500" />
                      )}
                    </div>
                    <div>
                      <h3 className="font-bold text-orange-900">Crime Game</h3>
                      <p className="text-xs text-orange-700">
                        {crimeStreak > 0 ? "You're on fire!" : "Solve a case to start!"}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-orange-600">
                      {crimeStreak}
                    </span>
                    <span className="text-xs text-orange-600 block font-medium">days</span>
                  </div>
                </div>

                <WeekStatus history={crimeHistory} activeColorClass="bg-orange-500" />

                <button
                  onClick={() => {
                    onClose();
                    navigate('/crime-game');
                  }}
                  className={`w-full mt-4 py-2.5 rounded-lg font-bold text-sm shadow-sm transition-all flex items-center justify-center gap-2 ${
                    crimeStreak > 0
                      ? 'bg-gradient-to-r from-orange-500 to-red-500 text-white hover:from-orange-600 hover:to-red-600'
                      : 'bg-orange-500 text-white hover:bg-orange-600 border border-orange-600'
                  }`}
                >
                  <Search className="w-4 h-4" />
                  {crimeStreak > 0 ? 'KEEP STREAK ALIVE' : 'SOLVE A CASE'}
                </button>
              </div>

              {/* Daily Training Section */}
              <div className="rounded-xl p-3 md:p-4 border bg-purple-50 border-purple-200 transition-colors">
                <div className="flex justify-between items-center mb-2 md:mb-3">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="p-1.5 md:p-2 rounded-lg bg-purple-100">
                      <Brain className="w-5 h-5 md:w-6 md:h-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-purple-900">Daily Training</h3>
                      <p className="text-xs text-purple-700">
                        {trainingStreak > 0 ? "Brain power up!" : "Train your brain today!"}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-purple-600">
                      {trainingStreak}
                    </span>
                    <span className="text-xs text-purple-600 block font-medium">days</span>
                  </div>
                </div>

                <WeekStatus history={trainingHistory} activeColorClass="bg-purple-500" />

                <button
                  onClick={() => {
                    onClose();
                    navigate('/training');
                  }}
                  className={`w-full mt-4 py-2.5 rounded-lg font-bold text-sm shadow-sm transition-all flex items-center justify-center gap-2 ${
                    trainingStreak > 0
                      ? 'bg-gradient-to-r from-purple-500 to-indigo-600 text-white hover:from-purple-600 hover:to-indigo-700'
                      : 'bg-purple-500 text-white hover:bg-purple-600 border border-purple-600'
                  }`}
                >
                  <Dumbbell className="w-4 h-4" />
                  {trainingStreak > 0 ? 'CONTINUE TRAINING' : 'START TRAINING'}
                </button>
              </div>

              {/* Word of the Day Section */}
              <div className="rounded-xl p-3 md:p-4 border bg-pink-50 border-pink-200 transition-colors">
                <div className="flex justify-between items-center mb-2 md:mb-3">
                  <div className="flex items-center gap-2 md:gap-3">
                    <div className="p-1.5 md:p-2 rounded-lg bg-pink-100">
                      <CalendarHeart className="w-5 h-5 md:w-6 md:h-6 text-pink-600" />
                    </div>
                    <div>
                      <h3 className="font-bold text-pink-900">Word of the Day</h3>
                      <p className="text-xs text-pink-700">
                        {wordDayStreak > 0 ? "Vocabulary Master!" : "Learn a new word!"}
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-2xl font-bold text-pink-600">
                      {wordDayStreak}
                    </span>
                    <span className="text-xs text-pink-600 block font-medium">days</span>
                  </div>
                </div>

                <WeekStatus history={wordDayHistory} activeColorClass="bg-pink-500" />

                <button
                  onClick={() => {
                    onClose();
                    navigate('/day');
                  }}
                  className={`w-full mt-4 py-2.5 rounded-lg font-bold text-sm shadow-sm transition-all flex items-center justify-center gap-2 ${
                    wordDayStreak > 0
                      ? 'bg-gradient-to-r from-pink-500 to-rose-600 text-white hover:from-pink-600 hover:to-rose-700'
                      : 'bg-pink-500 text-white hover:bg-pink-600 border border-pink-600'
                  }`}
                >
                  <Sparkles className="w-4 h-4" />
                  {wordDayStreak > 0 ? 'LEARN MORE' : 'START LEARNING'}
                </button>
              </div>
            </div>

            <button
              onClick={() => {
                onClose();
                navigate('/achievements');
              }}
              className="w-full mt-6 py-2 text-sm text-gray-500 hover:text-gray-700 font-medium transition-colors"
            >
              View All Achievements
            </button>
          </div>
        </div>
      </div>
    </>
  );
};
